import { Job, JobStatus, ProgressUpdate, Status } from "@api/apiClient";
import { makeAutoObservable, toJS } from "mobx";

// Add to the global window object for auto-completion in the console
// > window.JobStatusStore
export { };
declare global {
  interface Window {
    JobStatusStore: JobStatusStore;
  }
}

interface JobStatusWithProgress {
  jobId?: string;
  jobStatus?: JobStatus;
  progress?: ProgressUpdate;
}

class JobStatusStore {
  public projects: { [projectId: string]: { [modelId: string]: JobStatusWithProgress; }; } = {};

  constructor() {
    window.JobStatusStore = this;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get dump() {
    return toJS(this);
  }

  hasActiveJobs = (projectId?: string): boolean => {
    if (projectId) {
      return this.activeJobsCheck(projectId);
    }

    const activeJobs = Object.keys(this.projects).some((projectId) => {
      return this.activeJobsCheck(projectId);
    });
    console.log("hasActiveJobs", activeJobs);
    return activeJobs;
  };

  completeJob = (job: Job) => {
    if (job?.projectId && job?.modelId) {
      if (Object.hasOwnProperty.call(this.projects, job.projectId)) {
        if (Object.hasOwnProperty.call(this.projects[job.projectId], job.modelId!)) {
          this.projects[job.projectId][job.modelId] = {
            jobId: job.jobId,
            jobStatus: {
              workItemStatus: {
                status: "Success",
              },
            } as any,
            progress: undefined, // Clear progress on completion
          };
        }
      }
    }
  };

  failedJob = (job: Job) => {
    if (job?.projectId && job?.modelId) {
      if (Object.hasOwnProperty.call(this.projects, job.projectId)) {
        if (Object.hasOwnProperty.call(this.projects[job.projectId], job.modelId!)) {
          this.projects[job.projectId][job.modelId] = {
            jobId: job.jobId,
            jobStatus: {
              workItemStatus: {
                status: "Failed",
              },
            } as any,
            progress: undefined, // Clear progress on failure
          };
        }
      }
    }
  };

  private ensureModelEntry = (projectId: string, modelId: string): JobStatusWithProgress => {
    // Ensure project exists
    if (!Object.hasOwnProperty.call(this.projects, projectId)) {
      this.projects[projectId] = {};
    }

    // Ensure model entry exists
    if (!Object.hasOwnProperty.call(this.projects[projectId], modelId)) {
      this.projects[projectId][modelId] = {
        jobId: undefined,
        jobStatus: undefined,
        progress: undefined,
      };
    }

    return this.projects[projectId][modelId];
  };

  upsertStatus = (jobStatus: JobStatus) => {
    const projectId = jobStatus.job?.projectId;
    const modelId = jobStatus.job?.modelId;
    const jobId = jobStatus.job?.jobId;
    
    if (!projectId || !modelId) return;

    const entry = this.ensureModelEntry(projectId, modelId);
    entry.jobId = jobId;
    entry.jobStatus = jobStatus;
  };

  upsertProgress = (progressUpdate: ProgressUpdate) => {
    const projectId = progressUpdate.projectId;
    const modelId = progressUpdate.modelId;
    
    if (!projectId || !modelId) return;

    const entry = this.ensureModelEntry(projectId, modelId);
    if (!entry.jobId) {
      entry.jobId = progressUpdate.jobId;
    }
    entry.progress = progressUpdate;
  };

  getModelStatus = (projectId: string, modelId: string): JobStatus | undefined => {
    return this.projects[projectId]?.[modelId]?.jobStatus;
  };

  getModelProgress = (projectId: string, modelId: string): ProgressUpdate | undefined => {
    return this.projects[projectId]?.[modelId]?.progress;
  };

  private activeJobsCheck(projectId: string): boolean {
    if (Object.hasOwnProperty.call(this.projects, projectId)) {
      return Object.keys(this.projects[projectId]).some((modelId) => {
        const entry = this.projects[projectId][modelId];
        // Check if job is in progress/pending OR if there's active progress data (Import phase)
        const hasActiveJobStatus = entry?.jobStatus?.workItemStatus?.status === Status.Inprogress ||
          entry?.jobStatus?.workItemStatus?.status === Status.Pending;
        const hasActiveProgress = entry?.progress !== undefined && 
          entry?.progress.progress !== undefined && 
          entry?.progress.progress < 100;
        return hasActiveJobStatus || hasActiveProgress;
      });
    }
    return false;
  }
}

export default JobStatusStore;

